/** @file Store module for loading and storing submissions. */
import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { assert, sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
import { PeerFeedbackStore } from './peer_feedback';
const storeBuilder = getStoreBuilder();
/** @returns The initial state of the assignment. */
function makeInitialState() {
    return {
        byAssignment: {},
        testSubmissions: {},
        full: {},
        fullLoaders: {},
        latestSubmission: {},
        byAssignmentUser: {},
    };
}
const moduleBuilder = storeBuilder.module('submissions', makeInitialState());
/**
 * Keeps only the most recent submission for each user.
 *
 * @param data The paginated state containing the submissions to be cleaned.
 * @returns A new paginated state object with the filtered submissions.
 */
function cleanupByAssignmentState(data) {
    const sorted = sort.sortBy(data.data ?? [], sub => [sub.createdAt, sub.id], { reverse: true });
    const seenUserIds = new Set();
    const filtered = sorted.filter(sub => {
        if (seenUserIds.has(sub.user.id)) {
            return false;
        }
        seenUserIds.add(sub.user.id);
        return true;
    });
    return {
        ...data,
        data: filtered,
    };
}
/**
 * Sorts submissions, prioritizing group submissions, and removes duplicates.
 *
 * @param data The paginated state containing the submissions to be cleaned.
 * @returns A new paginated state object with the sorted and unique submissions.
 */
function cleanupByAssignmentUserState(data) {
    const filtered = Array.from(utils.itertools.uniqueSorted(sort.sortBy(data.data ?? [], sub => [sub.user.type === 'group-user' ? 1 : 0, sub.createdAt, sub.id], { reverse: true }), (a, b) => a.id === b.id));
    return {
        ...data,
        data: filtered,
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var SubmissionsStore;
(function (SubmissionsStore) {
    /**
     * Update the latest submissions state.
     *
     * @param state The state of the store.
     * @param root0 Data to update with.
     */
    function updateLatestSubmissions(state, { assignmentId, submissions, }) {
        const latestByUser = utils.vueSetDefault(state.latestSubmission, assignmentId, {});
        for (const submission of submissions) {
            for (const user of utils.user.containedUsersWithGroup(submission.user)) {
                const oldLatest = latestByUser[user.id];
                if (oldLatest == null || oldLatest.createdAt.isSameOrBefore(submission.createdAt)) {
                    utils.vueSet(latestByUser, user.id, submission);
                }
            }
        }
    }
    /**
     * Gets the test submission for a given assignment.
     *
     * This getter returns a nested `Maybe` to represent three distinct states:
     * 1. `Maybe.empty()`: The data is not yet available (i.e., loading).
     * 2. `Maybe.of(Maybe.empty())`: Loading is finished and no test submission exists.
     * 3. `Maybe.of(Maybe.of(submission))`: A test submission was found.
     *
     * It prioritizes explicitly fetched test submissions, falling back to any known submission
     * from a user that is marked as a test student.
     */
    SubmissionsStore.getTestSubmission = moduleBuilder.read(state => 
    /**
     * The actual getter.
     *
     * @param assignmentId The ID of the assignment for which to find the test submission.
     * @returns A nested `Maybe` as described above.
     */
    (assignmentId) => {
        const val = state.testSubmissions[assignmentId];
        if (val?.data != null && val.data.length > 0) {
            return Maybe.of(Maybe.of(val.data[0]));
        }
        const latestByAssig = state.latestSubmission[assignmentId];
        if (latestByAssig != null) {
            for (const sub of Object.values(latestByAssig)) {
                if (sub.user.isTestStudent) {
                    return Maybe.of(Maybe.of(sub));
                }
            }
        }
        if (val?.pagination?.tag === 'finished') {
            return Maybe.of(Maybe.empty());
        }
        return Maybe.empty();
    }, 'getTestSubmission');
    /** Clears all cached submission data for a specific assignment. */
    SubmissionsStore.commitClearForAssignment = moduleBuilder.commit((state, { assignmentId }) => {
        utils.vueDelete(state.byAssignment, assignmentId);
        utils.vueDelete(state.latestSubmission, assignmentId);
        utils.vueDelete(state.byAssignmentUser, assignmentId);
        for (const value of Object.values(state.full)) {
            if (value?.assignmentId === assignmentId) {
                utils.vueDelete(state.full, value.id);
            }
        }
    }, 'commitClearForAssignment');
    /** Upserts a full submission object into the state, updating all related caches. */
    SubmissionsStore.commitFullSubmission = moduleBuilder.commit((state, { data, }) => {
        const submission = new models.Submission(data);
        utils.vueSet(state.full, submission.id, submission);
        // BLAZE IT: R y a n C e l s i u s ° S o u n d s
        const byAssig = state.byAssignment[submission.assignmentId];
        if (byAssig != null) {
            // We should check that we are not adding a submission with a
            // duplicate id, since this method is called by patch.
            const filteredData = (byAssig.data ?? []).filter(sub => sub.id !== submission.id);
            utils.vueSet(state.byAssignment, submission.assignmentId, cleanupByAssignmentState({
                ...byAssig,
                data: [submission, ...filteredData],
            }));
        }
        const allByUser = utils.vueSetDefault(state.byAssignmentUser, submission.assignmentId, {});
        for (const user of utils.user.containedUsersWithGroup(submission.user)) {
            const byUser = allByUser[user.id];
            if (byUser != null) {
                utils.vueSet(state.byAssignmentUser[submission.assignmentId] ?? {}, user.id, cleanupByAssignmentUserState({
                    ...byUser,
                    data: [submission, ...(byUser.data ?? [])],
                }));
            }
            else if (data.isLatest) {
                utils.vueSet(allByUser, user.id, {
                    pagination: { tag: 'not-started' },
                    data: [submission],
                });
            }
        }
        if (data.isLatest) {
            updateLatestSubmissions(state, {
                assignmentId: submission.assignmentId,
                submissions: [submission],
            });
        }
        else {
            const latestByUser = state.latestSubmission[submission.assignmentId];
            if (latestByUser != null) {
                for (const user of utils.user.containedUsersWithGroup(submission.user)) {
                    const existing = latestByUser[user.id];
                    if (existing != null && existing.id === submission.id) {
                        utils.vueDelete(latestByUser, user.id);
                    }
                }
            }
        }
    }, 'commitFullSubmission');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getSingleSubmission = moduleBuilder.read(state => {
        const lookup = new Map(Object.values(state.byAssignment).flatMap(value => value?.data?.map(sub => [sub.id, sub]) ?? []));
        return (submissionId) => {
            const full = state.full[submissionId];
            if (full != null) {
                return Maybe.of(full);
            }
            return Maybe.fromNullable(lookup.get(submissionId));
        };
    }, 'getSingleSubmission');
    /** Gets the latest known submission for a specific user and assignment. */
    SubmissionsStore.getLatestSubmissionByUser = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param assignmentId The ID of the assignment.
     * @param userId The ID of the user.
     * @returns A `Maybe` containing the submission if one is found, otherwise empty. A
     * nothing might mean that this data isn't loaded yet.
     */
    (assignmentId, userId) => Maybe.fromNullable(state.latestSubmission[assignmentId]?.[userId]), 'getLatestSubmissionByUser');
    /** Gets the paginated list of latest submissions for an assignment, one per user. */
    SubmissionsStore.getLatestSubmissions = moduleBuilder.read(state => 
    /**
     * The actual getter function.
     *
     * @param assignmentId The ID of the assignment.
     * @returns A paginated state object containing the latest submission for each user.
     */
    (assignmentId) => utils.getPaginatedState(state.byAssignment[assignmentId]), 'getLatestSubmissions');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getGroupSubmissionOfUser = moduleBuilder.read(() => {
        const getLatest = SubmissionsStore.getLatestSubmissionByUser();
        return (assignmentId, userId) => getLatest(assignmentId, userId).filter(sub => sub.user.id !== userId);
    }, 'getGroupSubmissionOfUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getSubmissionsByUser = moduleBuilder.read(state => 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (assignmentId, userId) => utils.getPaginatedState(state.byAssignmentUser[assignmentId]?.[userId]), 'getSubmissionsByUser');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.getIsLatestSubmissionByUser = moduleBuilder.read(() => {
        const getLatestByUser = SubmissionsStore.getLatestSubmissionByUser();
        return (submission) => {
            const { assignmentId, user } = submission;
            let userId = user.id;
            if (user.type === 'group-user' && user.group.members.length > 0) {
                userId = user.group.members[0].id;
            }
            return getLatestByUser(assignmentId, userId).map(sub => sub.id === submission.id);
        };
    }, 'getIsLatestSubmissionByUser');
    const commitSingleSubmissionLoader = moduleBuilder.commit((state, payload) => {
        const { submissionId, loader } = payload;
        utils.vueSetOrDelete(state.fullLoaders, submissionId, Maybe.fromNullable(loader));
    }, 'commitSingleSubmissionLoader');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    SubmissionsStore.commitClearAll = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearAll');
    const commitSubmissions = moduleBuilder.commit((state, { assignmentId, data, }) => {
        if (data.data != null) {
            updateLatestSubmissions(state, { assignmentId, submissions: data.data });
        }
        utils.vueSet(state.byAssignment, assignmentId, cleanupByAssignmentState(data));
    }, 'commitSubmissions');
    const commitTestSubmissions = moduleBuilder.commit((state, { assignmentId, data, }) => {
        if (data.data != null) {
            updateLatestSubmissions(state, { assignmentId, submissions: data.data });
        }
        utils.vueSet(state.testSubmissions, assignmentId, cleanupByAssignmentState(data));
    }, 'commitTestSubmissions');
    const commitByUserSubmissions = moduleBuilder.commit((state, { assignmentId, userId, data, }) => {
        if (data.data != null && data.data.length > 0) {
            updateLatestSubmissions(state, { assignmentId, submissions: [data.data[0]] });
        }
        const byUser = utils.vueSetDefault(state.byAssignmentUser, assignmentId, {}) ?? {};
        utils.vueSet(byUser, userId, cleanupByAssignmentUserState(data));
    }, 'commitByUserSubmissions');
    /** Action to load submissions. */
    SubmissionsStore.loadSubmissions = moduleBuilder.dispatch(async (context, { assignmentId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitSubmissions({ assignmentId, data }),
            getData: () => context.state.byAssignment[assignmentId],
            load: async (nextToken) => {
                const result = await client.p.assignment.getAllSubmissions({
                    assignmentId,
                    nextToken,
                });
                return {
                    nextToken: result.nextToken,
                    data: result.data.map(sub => new models.Submission(sub)),
                };
            },
            toLoad,
        });
    }, 'loadSubmissions');
    /** Action to load a single submission. */
    SubmissionsStore.loadSingleSubmission = moduleBuilder.dispatch(async (context, { submissionId, force = false, }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitSingleSubmissionLoader({ submissionId, loader }),
            value: SubmissionsStore.getSingleSubmission()(submissionId),
            force,
            load: async () => {
                const { body } = await client.p.submission.get({ submissionId });
                assert.AssertionError.assert(body instanceof api.ExtendedWork, 'Data is of wrong type');
                SubmissionsStore.commitFullSubmission({
                    data: body,
                });
            },
            loader: Maybe.fromNullable(context.state.fullLoaders[submissionId]),
            removeLoader: () => commitSingleSubmissionLoader({ submissionId, loader: null }),
        });
    }, 'loadSingleSubmission');
    /** Action to load a list of selected submissions in an assignment. */
    SubmissionsStore.loadGivenSubmissions = moduleBuilder.dispatch(async (_, { submissionIds, }) => {
        await Promise.all(Array.from(submissionIds, submissionId => SubmissionsStore.loadSingleSubmission({ submissionId })));
    }, 'loadGivenSubmissions');
    /**
     * Fetches and stores the test submission(s) for a given assignment.
     *
     * This action fetches all pages of submissions made by a test student for the
     * specified assignment. It will not refetch data that has already been fully
     * loaded unless the `force` option is used.
     */
    SubmissionsStore.loadTestSubmission = moduleBuilder.dispatch(
    /**
     * The actual action handler.
     *
     * @param _ The module context (unused).
     * @param root0 The action payload.
     * @returns A promise that resolves when the operation is complete.
     */
    async (_, { assignmentId, force = false, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitTestSubmissions({ assignmentId, data }),
            toLoad: force ? 'force-all-pages' : 'all-pages',
            getData: () => SubmissionsStore.getTestSubmission()(assignmentId)
                .map((maybeSub) => ({
                pagination: { tag: 'finished' },
                data: maybeSub.toList(),
            }))
                .extract(),
            load: async (nextToken) => {
                const response = await client.p.assignment.getAllSubmissions({
                    assignmentId,
                    nextToken,
                    testStudent: true,
                });
                return {
                    nextToken: response.nextToken,
                    data: response.data.map(s => new models.Submission(s)),
                };
            },
        });
    }, 'loadTestSubmissions');
    /** Action to load submissions by a user. */
    SubmissionsStore.loadSubmissionsByUser = moduleBuilder.dispatch(async (context, { assignmentId, userId, toLoad, pageSize, }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitByUserSubmissions({ assignmentId, userId, data }),
            getData: () => context.state.byAssignmentUser[assignmentId]?.[userId],
            load: async (nextToken) => {
                const result = await client.p.assignment.getSubmissionsByUser({
                    assignmentId,
                    userId,
                    nextToken,
                    pageSize,
                });
                return {
                    nextToken: result.nextToken,
                    data: result.data.map(sub => new models.Submission(sub)),
                };
            },
            toLoad,
        });
        const submissions = context.state.byAssignmentUser[assignmentId]?.[userId]?.data ?? [];
        return { cgResult: submissions };
    }, 'loadSubmissionsByUser');
    /** Action to create a submission. */
    SubmissionsStore.createSubmission = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, authorId, isTestSubmission, ignoredFiles, requestBody } = payload;
        const response = await client.p.assignment.uploadSubmission({
            assignmentId,
            authorId,
            isTestSubmission,
            ignoredFiles,
            requestBody,
        });
        PeerFeedbackStore.commitClearConnections({ assignmentId });
        SubmissionsStore.commitFullSubmission({ data: response.cgResult });
        return response;
    }, 'createSubmission');
    /** Action to patch a submission. */
    SubmissionsStore.patchSubmission = moduleBuilder.dispatch(async (_, payload) => {
        const response = await client.p.submission.patch(payload);
        return response.map(cgResult => {
            const submission = new models.Submission(cgResult);
            SubmissionsStore.commitFullSubmission({ data: cgResult });
            return submission;
        });
    }, 'patchSubmission');
    const commitDeleteSubmission = moduleBuilder.commit((state, { submissionId, assignmentId, }) => {
        // 1) forget the full-loaded version
        utils.vueDelete(state.full, submissionId);
        // 2) remove from the general per-assignment list
        const byAssig = state.byAssignment[assignmentId];
        if (byAssig?.data != null) {
            utils.vueSet(state.byAssignment, assignmentId, cleanupByAssignmentState({
                ...byAssig,
                data: byAssig.data.filter(s => s.id !== submissionId),
            }));
        }
        // 3) remove from the test-submission list
        const testList = state.testSubmissions[assignmentId];
        if (testList?.data != null) {
            utils.vueSet(state.testSubmissions, assignmentId, cleanupByAssignmentState({
                ...testList,
                data: testList.data.filter(s => s.id !== submissionId),
            }));
        }
        // 4) remove from every per-user list for this assignment
        const byUserMap = state.byAssignmentUser[assignmentId] ?? {};
        for (const [userId, pag] of Object.entries(byUserMap)) {
            if (pag?.data != null) {
                utils.vueSet(byUserMap, Number(userId), cleanupByAssignmentUserState({
                    ...pag,
                    data: pag.data.filter(s => s.id !== submissionId),
                }));
            }
        }
        // 5) if it was ever the latest for a user, drop it
        const latestMap = state.latestSubmission[assignmentId] ?? {};
        for (const [userId, sub] of Object.entries(latestMap)) {
            if (sub.id === submissionId) {
                utils.vueDelete(latestMap, Number(userId));
            }
        }
    }, 'commitDeleteSubmission');
    /** Action to delete a submission. */
    SubmissionsStore.deleteSubmission = moduleBuilder.dispatch(async (_, payload) => {
        const { assignmentId, submissionId } = payload;
        const resp = await client.p.submission.delete({ submissionId });
        return resp.addCallbacks({
            onAfterSuccess: async () => {
                await SubmissionsStore.loadSubmissions({ assignmentId, toLoad: 'force' });
                commitDeleteSubmission(payload);
            },
        });
    }, 'deleteSubmission');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        SubmissionsStore.commitClearAll();
    });
})(SubmissionsStore || (SubmissionsStore = {}));
